/**
 * Browser-side preference sync hook
 * Handles background synchronization between IndexedDB and server
 * Supports offline-first with automatic retry and conflict resolution
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { PreferencePayload, PreferenceProof } from '@maco144/shared';
import { UsePreferenceStoreReturn } from './use-preference-store';

export type ConflictStrategy = 'server-wins' | 'client-wins' | 'manual';

export interface SyncState {
  isSyncing: boolean;
  lastSyncTime: number;
  worldRootAtSync: string;
  pendingChanges: Array<{
    key: string;
    timestamp: number;
    value: unknown;
  }>;
  failedChanges: Array<{
    key: string;
    value: unknown;
    error: string;
    retryCount: number;
  }>;
  syncError: string | null;
}

export interface SyncOptions {
  syncInterval?: number; // ms between syncs (default: 5000)
  retryAttempts?: number; // max retries per change (default: 3)
  retryDelay?: number; // ms delay between retries (default: 1000)
  conflictStrategy?: ConflictStrategy; // default: 'server-wins'
  enabled?: boolean; // enable/disable syncing (default: true)
}

/**
 * Hook for syncing preferences between local cache and server
 * Runs in background and handles network errors gracefully
 */
export function usePreferenceSync(
  store: UsePreferenceStoreReturn | null,
  userId?: string,
  wsUrl?: string,
  options: SyncOptions = {}
) {
  const {
    syncInterval = 5000,
    retryAttempts = 3,
    retryDelay = 1000,
    conflictStrategy = 'server-wins',
    enabled = true,
  } = options;

  const [syncState, setSyncState] = useState<SyncState>({
    isSyncing: false,
    lastSyncTime: 0,
    worldRootAtSync: '',
    pendingChanges: [],
    failedChanges: [],
    syncError: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const syncTimerRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountersRef = useRef<Map<string, number>>(new Map());
  const offlineLoggedRef = useRef(false); // Suppress repeated offline warnings
  const connectionFailuresRef = useRef(0); // Give up after 3 consecutive failures
  const connectionGivenUpRef = useRef(false);
  const pendingChangesRef = useRef<SyncState['pendingChanges']>([]);
  // Always points at the latest message handler so the socket (created async in
  // connectWebSocket) can dispatch to it without the handler being a dep.
  const handleServerMessageRef = useRef<((event: MessageEvent) => void) | null>(null);

  // Keep store and pendingChanges in refs so callbacks don't need them as deps.
  // usePreferenceStore returns a new object on every render; having it in
  // useCallback deps causes syncToServer/syncFromServer to recreate every render,
  // which re-triggers the periodic sync effect, which calls sync() immediately,
  // which calls setSyncState, which re-renders — an infinite loop.
  const storeRef = useRef(store);
  storeRef.current = store;
  pendingChangesRef.current = syncState.pendingChanges;

  // Track local changes for sync
  const addPendingChange = useCallback((key: string, value: unknown) => {
    setSyncState((prev) => {
      // Remove from failed if it was there
      const failed = prev.failedChanges.filter((c) => c.key !== key);

      // Add/update in pending
      const pending = prev.pendingChanges.filter((c) => c.key !== key);
      pending.push({
        key,
        timestamp: Date.now(),
        value,
      });

      return {
        ...prev,
        pendingChanges: pending,
        failedChanges: failed,
      };
    });
  }, []);

  // Connect to WebSocket
  // Offline-first: doesn't reject, allows local-only operation if server unavailable
  // Gives up after 3 consecutive failures to avoid browser console spam
  const connectWebSocket = useCallback(async () => {
    if (!wsUrl || !userId) return;
    if (connectionGivenUpRef.current) return null; // Already gave up — don't create more failing sockets

    return new Promise<WebSocket | null>((resolve) => {
      try {
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('[PreferenceSync] WebSocket connected ✓');
          offlineLoggedRef.current = false;
          connectionFailuresRef.current = 0;
          // Do NOT reset connectionGivenUpRef here — it may have been set by an
          // auth/rate-limit error message received after a successful open, and
          // resetting it would restart the reconnect storm on the next sync cycle.
          wsRef.current = ws;
          // Attach the inbound-message handler now that the socket exists.
          // (A mount-time effect can't: wsRef.current is null until here.)
          ws.onmessage = (event) => handleServerMessageRef.current?.(event);
          resolve(ws);
        };

        ws.onerror = () => {
          connectionFailuresRef.current += 1;
          if (!offlineLoggedRef.current) {
            console.log('[PreferenceSync] Server unavailable - using local-only mode');
            offlineLoggedRef.current = true;
          }
          if (connectionFailuresRef.current >= 3) {
            connectionGivenUpRef.current = true;
          }
          resolve(null);
        };

        ws.onclose = () => {
          wsRef.current = null;
        };

        // Set timeout for connection
        setTimeout(() => {
          if (!wsRef.current) {
            connectionFailuresRef.current += 1;
            if (!offlineLoggedRef.current) {
              console.log('[PreferenceSync] Server unavailable - using local-only mode');
              offlineLoggedRef.current = true;
            }
            if (connectionFailuresRef.current >= 3) {
              connectionGivenUpRef.current = true;
            }
            resolve(null);
          }
        }, 5000);
      } catch (err) {
        if (!offlineLoggedRef.current) {
          console.log('[PreferenceSync] Server unavailable - using local-only mode');
          offlineLoggedRef.current = true;
        }
        resolve(null);
      }
    });
  }, [wsUrl, userId]);

  // Send message to server
  const sendMessage = useCallback((message: unknown) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('WebSocket not connected'));
    }

    return new Promise<void>((resolve, reject) => {
      try {
        wsRef.current!.send(JSON.stringify(message));
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  // Resolve conflict between local and server versions
  const resolveConflict = useCallback(
    (local: PreferencePayload, server: PreferencePayload): PreferencePayload => {
      switch (conflictStrategy) {
        case 'server-wins':
          return server;
        case 'client-wins':
          return local;
        case 'manual':
          // Manual resolution: prefer server's timestamp but keep client's value if newer
          if (local.timestamp && server.timestamp) {
            return local.timestamp > server.timestamp ? local : server;
          }
          return server;
        default:
          return server;
      }
    },
    [conflictStrategy]
  );

  // Sync local changes to server
  // Offline-first: keeps changes local if server unavailable
  const syncToServer = useCallback(async () => {
    if (!storeRef.current || !userId || !enabled) return;

    setSyncState((prev) => ({ ...prev, isSyncing: true }));

    try {
      // Try to connect if not already connected
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        await connectWebSocket();
      }

      // If no server available, just mark as pending (offline mode)
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        setSyncState((prev) => ({ ...prev, isSyncing: false }));
        return;
      }

      // Read pendingChanges via a ref snapshot to avoid stale closure
      // (syncState is not in deps, so it would be stale if read directly)
      const pendingChanges = pendingChangesRef.current;

      for (const change of pendingChanges) {
        const retryCount = retryCountersRef.current.get(change.key) || 0;

        if (retryCount >= retryAttempts) {
          // Max retries exceeded, move to failed
          setSyncState((prev) => ({
            ...prev,
            pendingChanges: prev.pendingChanges.filter((c) => c.key !== change.key),
            failedChanges: [
              ...prev.failedChanges,
              {
                key: change.key,
                value: change.value,
                error: 'Max retries exceeded',
                retryCount,
              },
            ],
          }));
          continue;
        }

        try {
          // Send to server
          await sendMessage({
            type: 'set_preference',
            userId,
            key: change.key,
            value: change.value,
          });

          // Mark as synced
          setSyncState((prev) => ({
            ...prev,
            pendingChanges: prev.pendingChanges.filter((c) => c.key !== change.key),
            lastSyncTime: Date.now(),
            syncError: null,
          }));

          retryCountersRef.current.delete(change.key);
        } catch (err) {
          // Increment retry count
          const newRetryCount = retryCount + 1;
          retryCountersRef.current.set(change.key, newRetryCount);

          console.warn(
            `[PreferenceSync] Failed to sync ${change.key}, retry ${newRetryCount}/${retryAttempts}:`,
            err
          );
        }
      }

      setSyncState((prev) => ({ ...prev, isSyncing: false }));
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Unknown error';
      setSyncState((prev) => ({
        ...prev,
        isSyncing: false,
        syncError: error,
      }));

      console.error('[PreferenceSync] Sync failed:', error);
    }
  }, [userId, enabled, connectWebSocket, sendMessage, retryAttempts]);

  // Sync server changes to local
  // Offline-first: skips if server unavailable
  const syncFromServer = useCallback(async () => {
    if (!storeRef.current || !userId || !enabled) return;

    try {
      // Try to connect if not already connected
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        await connectWebSocket();
      }

      // Skip if no server available (offline mode)
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return;
      }

      // Request all preferences from server
      await sendMessage({
        type: 'get_preferences',
        userId,
      });

      // Server will respond with preference_loaded message
      // Which is handled by the message listener below
    } catch (err) {
      // Silently ignore - expected in offline mode
    }
  }, [userId, enabled, connectWebSocket, sendMessage]);

  // Handle messages from server
  const handleServerMessage = useCallback(
    async (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data) as any;

        switch (message.type) {
          case 'preference_updated': {
            // Server confirmed our update
            const { key, proof, worldRoot } = message;
            setSyncState((prev) => ({
              ...prev,
              worldRootAtSync: worldRoot,
            }));
            break;
          }

          case 'preferences_loaded': {
            // Server sent preferences (bulk load)
            const { preferences } = message;
            if (storeRef.current && preferences) {
              for (const pref of preferences) {
                // Resolve conflicts
                const local = await storeRef.current.getPreference(pref.key);
                const resolved = local ? resolveConflict(local, pref) : pref;

                // Update local store
                await storeRef.current.setPreference(pref.key, resolved.value);
              }

              setSyncState((prev) => ({
                ...prev,
                lastSyncTime: Date.now(),
              }));
            }
            break;
          }

          case 'sync_complete': {
            // Server acknowledged sync
            const { worldRoot } = message;
            setSyncState((prev) => ({
              ...prev,
              lastSyncTime: Date.now(),
              worldRootAtSync: worldRoot,
            }));
            break;
          }

          case 'preference_conflict': {
            // Server detected conflict
            const { key, serverValue } = message;
            if (storeRef.current) {
              const local = await storeRef.current.getPreference(key);
              if (local) {
                const resolved = resolveConflict(local, {
                  ...local,
                  value: serverValue,
                });
                await storeRef.current.setPreference(key, resolved.value);
              }
            }
            break;
          }

          case 'error': {
            const { message: errorMsg } = message;
            // Auth and rate-limit errors are permanent for this session — give up
            // immediately rather than looping. Updating syncState here would recreate
            // syncToServer/syncFromServer callbacks, re-trigger the periodic sync
            // effect, and cause an infinite reconnect storm.
            if (
              typeof errorMsg === 'string' &&
              (errorMsg.includes('Not authenticated') ||
                errorMsg.includes('Rate limit') ||
                errorMsg.includes('Unauthorized'))
            ) {
              connectionGivenUpRef.current = true;
              wsRef.current?.close();
              wsRef.current = null;
              // Don't setSyncState — avoids the callback/effect churn loop
              break;
            }
            setSyncState((prev) => ({
              ...prev,
              syncError: errorMsg,
            }));
            console.error('[PreferenceSync] Server error:', errorMsg);
            break;
          }
        }
      } catch (err) {
        console.error('[PreferenceSync] Failed to handle message:', err);
      }
    },
    [resolveConflict]
  );

  // Keep the socket's message handler current. The socket dispatches via this
  // ref (set in connectWebSocket.onopen), so inbound messages are handled even
  // though the socket is created after mount.
  handleServerMessageRef.current = handleServerMessage;

  // Setup periodic sync
  useEffect(() => {
    if (!enabled || !userId || !storeRef.current) return;

    const sync = async () => {
      if (!navigator.onLine) {
        return; // Skip sync if offline
      }

      try {
        // Sync both directions
        await syncFromServer();
        await syncToServer();
      } catch (err) {
        console.error('[PreferenceSync] Periodic sync failed:', err);
      }
    };

    // Initial sync
    sync();

    // Periodic syncs
    syncTimerRef.current = setInterval(sync, syncInterval);

    return () => {
      if (syncTimerRef.current) {
        clearInterval(syncTimerRef.current);
      }
    };
  }, [enabled, userId, syncInterval, syncToServer, syncFromServer]);

  // Sync when going online
  useEffect(() => {
    const handleOnline = async () => {
      console.log('[PreferenceSync] Online, starting sync');
      await syncToServer();
      await syncFromServer();
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncToServer, syncFromServer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (syncTimerRef.current) {
        clearInterval(syncTimerRef.current);
      }
    };
  }, []);

  return {
    // State
    isSyncing: syncState.isSyncing,
    lastSyncTime: syncState.lastSyncTime,
    worldRootAtSync: syncState.worldRootAtSync,
    pendingChanges: syncState.pendingChanges,
    failedChanges: syncState.failedChanges,
    syncError: syncState.syncError,

    // Derived state
    hasPendingChanges: syncState.pendingChanges.length > 0,
    hasFailedChanges: syncState.failedChanges.length > 0,
    isPending: syncState.isSyncing || syncState.pendingChanges.length > 0,

    // Operations
    addPendingChange,
    syncToServer,
    syncFromServer,
    retryFailed: () => {
      retryCountersRef.current.clear();
      return syncToServer();
    },
  };
}

export type UsePreferenceSyncReturn = ReturnType<typeof usePreferenceSync>;
